import * as React from 'react';

import { MultiSelectFilter } from '@rematter/ui-web';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import { FilterLabel } from '../../_shared/FilterLabels';

import { BillAccountRecord } from '~/modules/finance/_graphql/queryBillAccounts';
import {
  $accountFilterSearchTerm,
  $columnSelectedAccountIds,
  $remoteFilterAccounts,
} from '~/modules/finance/_state/financeInvoices.state';
import { useTranslations } from '~/modules/finance/i18n/useTranslations';

interface InvoiceAccountColumnFilterProps {
  onClose?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ INVOICE ACCOUNT COLUMN FILTER ⚛️ ⚛️ ⚛️
//
export const InvoiceAccountColumnFilter: React.FC<
  InvoiceAccountColumnFilterProps
> = ({ onClose }) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const {
    data: accountsData,
    isFetching: isFetchingAccounts,
    isLoading: isLoadingAccounts,
  } = useAtomValue($remoteFilterAccounts);
  const [selectedAccountIdsSet, setSelectedAccountIdsSet] = useAtom(
    $columnSelectedAccountIds,
  );
  const setSearchTerm = useSetAtom($accountFilterSearchTerm);

  // Memoized values
  const selectedAccountIds = React.useMemo(
    () => Array.from(selectedAccountIdsSet),
    [selectedAccountIdsSet],
  );

  const selectedKeys = React.useMemo(
    () => new Set<React.Key>(selectedAccountIds),
    [selectedAccountIds],
  );

  const accounts = React.useMemo(
    () => accountsData?.accounts ?? [],
    [accountsData],
  );

  const selectedAccounts = React.useMemo(
    () => accounts.filter((account) => selectedAccountIds.includes(account.id)),
    [accounts, selectedAccountIds],
  );

  // Callbacks
  const handleSearch = React.useCallback(
    (query: string) => {
      setSearchTerm(query || undefined);
    },
    [setSearchTerm],
  );

  const handleSelectionChange = React.useCallback(
    (keys: Set<React.Key>) => {
      setSelectedAccountIdsSet(new Set(Array.from(keys) as string[]));
    },
    [setSelectedAccountIdsSet],
  );

  const handleReset = React.useCallback(() => {
    setSelectedAccountIdsSet(new Set());
  }, [setSelectedAccountIdsSet]);

  const getLabel = React.useCallback(
    (item: BillAccountRecord, isSelected: boolean) => (
      <FilterLabel label={item.name} type={item.type} isSelected={isSelected} />
    ),
    [],
  );

  return (
    <MultiSelectFilter
      className="@InvoiceAccountColumnFilter min-w-[350px]"
      entries={accounts}
      selectedEntries={selectedAccounts}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      getEntryKey={(account) => account.id}
      getEntryLabel={getLabel}
      onSearch={handleSearch}
      onDismiss={onClose}
      onReset={handleReset}
      searchPlaceholder={t.invoices.list.search.placeholder}
      isLoading={isLoadingAccounts}
      isFetching={isFetchingAccounts}
    />
  );
};
